import React, { memo, useState, useCallback } from 'react';
import type { Question } from '../../types';
import { QuestionImageOrVideo } from './QuestionImageOrVideo';

interface HotspotQuestionProps {
  question: Question;
  questionNumber?: number;
  selectedAnswer?: string;
  isAnswered?: boolean;
  onAnswer?: (answerId: string) => void;
  streakMessage?: string | null;
}

/**
 * HotspotQuestion — Visual question type for sign identification
 *
 * Shows sign image prominently, options as tap tiles (no letter prefix).
 * Used for all sign/marking questions that have an imageUrl.
 * Mimics real CBR exam visual format.
 */
export const HotspotQuestion: React.FC<HotspotQuestionProps> = memo(({
  question,
  questionNumber = 1,
  selectedAnswer,
  isAnswered,
  onAnswer,
  streakMessage,
}) => {
  const [lastClickTime, setLastClickTime] = useState(0);

  const handleOptionClick = useCallback((optionId: string) => {
    if (isAnswered) return;
    const now = Date.now();
    if (now - lastClickTime < 200) return;
    setLastClickTime(now);
    onAnswer?.(optionId);
  }, [isAnswered, lastClickTime, onAnswer]);

  return (
    <div className="hotspot-question">
      {/* Sign image — full-width, prominent */}
      {question.imageUrl && (
        <div className="hotspot-image-container">
          <QuestionImageOrVideo
            url={question.imageUrl}
            alt={question.imageHint || 'road sign'}
            className="hotspot-image"
          />
        </div>
      )}

      {/* Question text */}
      <div className="hotspot-question-text">
        <span className="professional-question-number">{questionNumber})</span>{' '}
        {question.text}
      </div>

      {/* Visual tap tiles with consistent option lettering */}
      <div className="hotspot-options">
        {question.options.slice(0, 4).map((option, idx) => {
          const isCorrect = isAnswered && question.correctAnswerId === option.id;
          const isWrong = isAnswered && selectedAnswer === option.id && selectedAnswer !== question.correctAnswerId;
          const isSelected = selectedAnswer === option.id;

          return (
            <button
              key={option.id}
              type="button"
              className={[
                'hotspot-option',
                'practice-option-base',
                isSelected && !isAnswered ? 'hotspot-option--selected' : '',
                isCorrect ? 'hotspot-option--correct' : '',
                isWrong ? 'hotspot-option--wrong' : '',
                isAnswered && !isSelected ? 'hotspot-option--dimmed' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => handleOptionClick(option.id)}
              disabled={isAnswered}
              aria-pressed={isSelected}
              aria-label={`Option ${String.fromCharCode(65 + idx)}: ${option.text}`}
            >
              <span className="hotspot-option-letter">{String.fromCharCode(65 + idx)}</span>
              {isCorrect && <span className="hotspot-option-icon">✓</span>}
              {isWrong && <span className="hotspot-option-icon">✗</span>}
              <span className="hotspot-option-text">{option.text}</span>
            </button>
          );
        })}
      </div>

      {streakMessage && (
        <div className="practice-streak-message">{streakMessage}</div>
      )}
    </div>
  );
});

HotspotQuestion.displayName = 'HotspotQuestion';
